/**
 * MyTravel Page — 我的旅行
 *
 * 功能：用户旅行记录、收藏、偏好、AI记忆
 */

import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { MapPin, Heart, Clock, Sparkles, Plane, Settings, Bookmark } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useTrips } from '@/hooks/useTrips';
import { useUserTravelProfile } from '@/hooks/useRecommendations';

const MyTravel = () => {
  const { user } = useAuth();
  const { data: trips } = useTrips(user?.id);
  const { data: profile } = useUserTravelProfile(user?.id);
  const [activeTab, setActiveTab] = useState('trips');

  if (!user) {
    return (
      <div className="min-h-screen bg-bg-primary">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 md:px-6 pt-24 pb-20 text-center">
          <Plane className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h1 className="text-2xl font-bold text-text-primary mb-2">登录查看你的旅行</h1>
          <p className="text-text-secondary mb-6">记录每一次旅程，让旅行更有意义</p>
          <Button onClick={() => window.location.hash = '/login'} className="bg-primary text-white">
            立即登录
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-24 pb-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-text-primary">我的旅行</h1>
            <p className="text-text-secondary mt-1">管理你的行程、收藏和偏好</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => window.location.hash = '/settings'}>
              <Settings className="w-4 h-4 mr-1" /> 偏好设置
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="p-4 rounded-xl bg-white shadow-sm border border-gray-50">
            <div className="flex items-center gap-2 text-primary mb-2">
              <Plane className="w-5 h-5" />
              <span className="text-sm font-medium">行程数</span>
            </div>
            <p className="text-2xl font-bold text-text-primary">{trips?.length || 0}</p>
          </div>
          <div className="p-4 rounded-xl bg-white shadow-sm border border-gray-50">
            <div className="flex items-center gap-2 text-rose-500 mb-2">
              <Heart className="w-5 h-5" />
              <span className="text-sm font-medium">收藏</span>
            </div>
            <p className="text-2xl font-bold text-text-primary">0</p>
          </div>
          <div className="p-4 rounded-xl bg-white shadow-sm border border-gray-50">
            <div className="flex items-center gap-2 text-emerald-500 mb-2">
              <MapPin className="w-5 h-5" />
              <span className="text-sm font-medium">探索城市</span>
            </div>
            <p className="text-2xl font-bold text-text-primary">
              {profile?.searchedCities?.length || 0}
            </p>
          </div>
          <div className="p-4 rounded-xl bg-white shadow-sm border border-gray-50">
            <div className="flex items-center gap-2 text-amber-500 mb-2">
              <Sparkles className="w-5 h-5" />
              <span className="text-sm font-medium">AI 记忆</span>
            </div>
            <p className="text-2xl font-bold text-text-primary">0</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 bg-white rounded-xl p-1">
            <TabsTrigger value="trips" className="rounded-lg">
              <Plane className="w-4 h-4 mr-1" /> 我的行程
            </TabsTrigger>
            <TabsTrigger value="favorites" className="rounded-lg">
              <Bookmark className="w-4 h-4 mr-1" /> 收藏
            </TabsTrigger>
            <TabsTrigger value="preferences" className="rounded-lg">
              <Settings className="w-4 h-4 mr-1" /> 偏好
            </TabsTrigger>
            <TabsTrigger value="memory" className="rounded-lg">
              <Sparkles className="w-4 h-4 mr-1" /> AI 记忆
            </TabsTrigger>
          </TabsList>

          <TabsContent value="trips">
            <div className="space-y-4">
              {trips && trips.length > 0 ? (
                trips.map((trip) => (
                  <div
                    key={trip.id}
                    onClick={() => window.location.hash = `/trip/${trip.id}`}
                    className="p-4 rounded-xl bg-white shadow-sm border border-gray-50 hover:shadow-md hover:border-primary/20 transition-all cursor-pointer"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-text-primary">{trip.title}</h3>
                        <p className="text-sm text-gray-400 mt-1">
                          {trip.destination} · {trip.days}天
                        </p>
                      </div>
                      <Clock className="w-4 h-4 text-gray-300" />
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-16 bg-white rounded-xl">
                  <Plane className="w-12 h-12 mx-auto mb-3 text-gray-200" />
                  <p className="text-gray-400">还没有行程</p>
                  <Button
                    className="mt-4 bg-primary text-white"
                    onClick={() => window.location.hash = '/planner'}
                  >
                    <Sparkles className="w-4 h-4 mr-1" /> 创建第一个行程
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="favorites">
            <div className="text-center py-16 bg-white rounded-xl">
              <Heart className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400">收藏的地点会出现在这里</p>
              <p className="text-xs text-gray-300 mt-1">探索目的地时点击收藏</p>
            </div>
          </TabsContent>

          <TabsContent value="preferences">
            <div className="p-6 rounded-xl bg-white shadow-sm border border-gray-50">
              <h3 className="font-medium text-text-primary mb-4">旅行偏好</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-gray-50">
                  <span className="text-sm text-text-secondary">预算偏好</span>
                  <span className="text-sm text-primary">
                    {profile?.preferredPriceLevel ? '¥'.repeat(profile.preferredPriceLevel) : '未设置'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-50">
                  <span className="text-sm text-text-secondary">旅行标签</span>
                  <span className="text-sm text-gray-400">
                    {profile?.preferredTags?.length ? profile.preferredTags.join(', ') : '未设置'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-text-secondary">搜索历史</span>
                  <span className="text-sm text-primary">{profile?.totalSearches || 0} 次</span>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-4">
                编辑偏好
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="memory">
            <div className="text-center py-16 bg-white rounded-xl">
              <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400">AI 记忆功能</p>
              <p className="text-xs text-gray-300 mt-1">AI 会记住你的旅行偏好，为你提供更精准的建议</p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MyTravel;
